/**
 * Горячие клавиши: таблица привязок и чистый резолвер.
 *
 * Модуль чистый — ни DOM, ни React. Слушатель живёт в store/hotkeys.ts, и
 * разделение здесь не косметическое: только так правила проверяются обычным
 * node-тестом, а таблица подсказки читается из ТОГО ЖЕ источника, что и
 * резолвер, и потому не может с ним разойтись.
 *
 * # Почему привязка по code, а не по key
 *
 * Интерфейс целиком русский. На раскладке ЙЦУКЕН клавиши Q W A S дают «й ц ф ы»,
 * и таблица по event.key молча перестала бы работать в тот момент, когда
 * оператор переключил раскладку, чтобы набрать название рынка. KeyboardEvent.code
 * описывает ФИЗИЧЕСКОЕ положение клавиши и от раскладки не зависит.
 *
 * На физическом положении держится и геометрия: Q W над A S — это блок 2×2,
 * повторяющий сетку кнопок на экране. РЯД — ИСХОД (верхний / нижний, в порядке
 * outcome_index рынка), КОЛОНКА — роль в сделке (слева Maker L1, справа Taker).
 * Одинаковая форма у экрана и у клавиатуры — то единственное, что делает
 * таблицу запоминаемой без шпаргалки.
 *
 * Так было не всегда: до перестройки панели ряд означал СТОРОНУ (покупка
 * сверху, продажа снизу), а исход выбирался отдельной клавишей. Панель тогда
 * работала по одному выбранному исходу, и на пятиминутном рынке это стоило
 * лишнего переключения перед каждой ставкой.
 *
 * # Политика модификаторов
 *
 * Ctrl / meta / alt делают привязку НЕсовпавшей — ⌘Q, Ctrl+W, ⌘R, ⌘L остаются
 * за браузером и системой целиком. Именно поэтому сторона не может уехать в
 * Ctrl и уезжает в Shift.
 *
 * Shift ЗНАЧИМ и выбирает сторону: без него покупка, с ним продажа. Асимметрия
 * намеренная — покупка это вход, продажа это выход из УЖЕ ЗАНЯТОЙ позиции, и
 * лишнее физическое усилие стоит на том действии, которое тратит существующую
 * позицию, а не на том, которое проверит риск-гейт. Продажа к тому же
 * бессмысленна без позиции, и при её отсутствии клавиша отвечает эхом, а не
 * ордером.
 */

import type { PriceMode } from "./quick";
import type { Side } from "../api/types";

/**
 * Номер исхода в порядке outcome_index рынка: 0 — верхний ряд, 1 — нижний.
 *
 * Не asset_id: таблица привязок статична и не знает ни о каком рынке, а
 * подставить токен по номеру умеет панель, у которой рынок как раз есть.
 */
export type OutcomeRow = 0 | 1;

export type HotkeyAction =
  | { type: "quick"; outcomeIndex: OutcomeRow; side: Side; mode: PriceMode }
  | { type: "size"; index: number }
  | { type: "outcome" }
  | { type: "escape" }
  | { type: "help" }
  | { type: "cancelMarket" };

export interface Binding {
  /** KeyboardEvent.code — физическое положение клавиши. */
  code: string;
  /** Нужен ли Shift. Прочие модификаторы запрещены всегда. */
  shift?: boolean;
  /** Что рисовать оператору. */
  label: string;
  /** Что даёт эта клавиша на русской раскладке — для подсказки. */
  layoutHint?: string;
  description: string;
  action: HotkeyAction;
  /** Показывать ли в оверлее подсказки (цифры сворачиваются в одну строку). */
  hidden?: boolean;
}

export const HOTKEYS: readonly Binding[] = [
  {
    code: "KeyQ",
    label: "Q",
    layoutHint: "й",
    description: "купить верхний исход: Maker L1",
    action: { type: "quick", outcomeIndex: 0, side: "BUY", mode: "join" },
  },
  {
    code: "KeyW",
    label: "W",
    layoutHint: "ц",
    description: "купить верхний исход: Taker",
    action: { type: "quick", outcomeIndex: 0, side: "BUY", mode: "cross" },
  },
  {
    code: "KeyA",
    label: "A",
    layoutHint: "ф",
    description: "купить нижний исход: Maker L1",
    action: { type: "quick", outcomeIndex: 1, side: "BUY", mode: "join" },
  },
  {
    code: "KeyS",
    label: "S",
    layoutHint: "ы",
    description: "купить нижний исход: Taker",
    action: { type: "quick", outcomeIndex: 1, side: "BUY", mode: "cross" },
  },
  {
    code: "KeyQ",
    shift: true,
    label: "⇧Q",
    layoutHint: "й",
    description: "продать верхний исход (выбранной долей): Maker L1",
    action: { type: "quick", outcomeIndex: 0, side: "SELL", mode: "join" },
  },
  {
    code: "KeyW",
    shift: true,
    label: "⇧W",
    layoutHint: "ц",
    description: "продать верхний исход: Taker",
    action: { type: "quick", outcomeIndex: 0, side: "SELL", mode: "cross" },
  },
  {
    code: "KeyA",
    shift: true,
    label: "⇧A",
    layoutHint: "ф",
    description: "продать нижний исход: Maker L1",
    action: { type: "quick", outcomeIndex: 1, side: "SELL", mode: "join" },
  },
  {
    code: "KeyS",
    shift: true,
    label: "⇧S",
    layoutHint: "ы",
    description: "продать нижний исход: Taker",
    action: { type: "quick", outcomeIndex: 1, side: "SELL", mode: "cross" },
  },
  {
    code: "KeyZ",
    label: "Z",
    layoutHint: "я",
    // Панель быстрых действий работает по ОБОИМ исходам сразу и выбранного не
    // имеет. Выбранный исход остался у формы ордера и у клика по лесенке, и Z
    // переключает именно его — эффект видимый (меняется выпадающий список
    // формы), поэтому клавиша не стала молчаливой и остаётся.
    description: "переключить исход формы ордера",
    action: { type: "outcome" },
  },
  {
    code: "Escape",
    label: "Esc",
    description: "выключить боевой режим; из поля ввода — снять фокус",
    action: { type: "escape" },
  },
  {
    code: "Slash",
    shift: true,
    label: "?",
    description: "показать эту подсказку",
    action: { type: "help" },
  },
  {
    code: "KeyC",
    shift: true,
    label: "Shift+C",
    layoutHint: "с",
    description: "снять все ордера по рынку у всех аккаунтов (сразу, без подтверждения)",
    action: { type: "cancelMarket" },
  },
  // Цифры: девять привязок, одна строка в подсказке.
  ...Array.from({ length: 9 }, (_, i): Binding => ({
    code: `Digit${i + 1}`,
    label: String(i + 1),
    description: `выбрать пресет размера №${i + 1}`,
    action: { type: "size", index: i },
    hidden: true,
  })),
  ...Array.from({ length: 9 }, (_, i): Binding => ({
    code: `Numpad${i + 1}`,
    label: String(i + 1),
    description: `выбрать пресет размера №${i + 1}`,
    action: { type: "size", index: i },
    hidden: true,
  })),
];

/** Достаточное описание события для резолвера. DOM здесь не нужен. */
export interface KeyEventLike {
  code: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** Автоповтор зажатой клавиши. */
  repeat: boolean;
  /** Идёт набор через IME. */
  isComposing?: boolean;
}

/** Достаточное описание цели события. */
export interface TargetInfo {
  tag: string;
  contentEditable: boolean;
}

/**
 * Печатает ли оператор прямо сейчас.
 *
 * FAIL-CLOSED по типу поля: годится ЛЮБОЙ input, включая чекбокс и кнопку.
 * Потерять хоткей из-за того, что фокус стоял на чекбоксе, — безобидно;
 * отправить ордер потому, что он там стоял, — нет.
 *
 * Обхода предков не нужно: isContentEditable уже отражает унаследованную
 * редактируемость.
 */
export function isTyping(t: TargetInfo | null | undefined): boolean {
  if (!t) return false;
  const tag = t.tag.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.contentEditable;
}

/**
 * Находит привязку под событие.
 *
 * Возвращает null, если модификаторы не те, клавиша неизвестна, клавиша зажата
 * (автоповтор) или идёт набор через IME.
 *
 * Автоповтор игнорируется не из аккуратности: зажатая W иначе строчила бы
 * тейкерами через спред десятками в секунду. Это самый вероятный способ
 * потерять деньги на хоткеях, и он же самый простой в защите.
 */
export function resolveHotkey(e: KeyEventLike): Binding | null {
  if (e.repeat) return null;
  if (e.isComposing === true) return null;
  // ctrl/meta/alt отдаются браузеру и системе целиком.
  if (e.ctrlKey || e.metaKey || e.altKey) return null;

  for (const b of HOTKEYS) {
    if (b.code !== e.code) continue;
    if ((b.shift === true) !== e.shiftKey) continue;
    return b;
  }
  return null;
}

/**
 * Проходит ли действие сквозь защиту от набора текста.
 *
 * Escape — единственное исключение, и смысл у него меняется: в поле ввода он
 * снимает фокус, вне поля — выключает боевой режим. Наименее удивительное
 * поведение из возможных.
 */
export function allowedWhileTyping(a: HotkeyAction): boolean {
  return a.type === "escape";
}

/** Привязки для оверлея подсказки, без девяти цифровых строк. */
export function visibleBindings(): Binding[] {
  return HOTKEYS.filter((b) => b.hidden !== true);
}
